import dot from 'dot-prop-immutable'
import omit from 'lodash/omit'
import { AnyAction } from 'redux'

import {
  defaultReplyUpdatedNotification,
  loadDefaultReply,
  updateTriggerDefaultReplyEnabled,
} from 'apps/defaultReply/store/actions'
import * as externalTriggersAtypes from 'apps/externalTriggers/externalTriggersReduxActionTypes'
import { loadFlow } from 'apps/followReply/store/actions/loadFlow'
import { toggleFlow } from 'apps/followReply/store/actions/toggleFlow'
import * as gtAtypes from 'apps/growthTools/constants/gtReduxActionTypes'
import { keywordsActions } from 'apps/keywords/keywordsSlice'
import { addPost } from 'apps/posting/actions/postActions'
import { ensurePostList, loadPostList } from 'apps/posting/actions/postListActions'
import * as smrTypes from 'apps/storyMentionReply/actionTypes'
import { followWelcomeAdapter } from 'apps/tiktokFollowWelcome/adapter'
import { loadTikTokFlow } from 'apps/tiktokFollowWelcome/store/actions/loadTikTokFlow'
import { removeTikTokFlow } from 'apps/tiktokFollowWelcome/store/actions/removeTikTokFlow'
import { setTikTokFlow } from 'apps/tiktokFollowWelcome/store/actions/setTikTokFlow'
import { tikTokTriggerUpdated } from 'apps/tiktokFollowWelcome/store/actions/tikTokTriggerUpdated'
import { toggleTikTokFlow } from 'apps/tiktokFollowWelcome/store/actions/toggleTikTokFlow'
import {
  createdTriggerRuleNotification,
  createTriggerRule,
  deleteTriggerRule,
  updateTriggerRuleNotification,
} from 'apps/triggerRules/actions/triggerRulesActions'
import { deletedNotification } from 'apps/triggerRules/slices/triggerRuleListSlice'
import * as welcomeMessageAtypes from 'apps/welcomeMessage/welcomeMessageReduxActionTypes'
import { cartReplyActionTypes } from 'apps/whatsApp/redux/cartReplySlice'
import * as builderAtypes from 'common/builder/constants/builderReduxActionTypes'
import * as atypes from 'common/cms/constants/cmsReduxActionTypes'
import * as flowAtypes from 'common/flow/constants/flowReduxActionTypes'
import { TriggerType } from 'common/flow/constants/TriggerType'
import { ExportFlowStatuses, FlowStatus } from 'common/flow/flowConstants'
import { isFullFlow } from 'common/flow/flowHelpers'
import { IFlow, IFlowTrigger } from 'common/flow/flowInterfaces'
import { ITriggerId } from 'common/flow/flowTriggersInterfaces'
import { parseFlow } from 'common/flow/models/Flow/parseFlow'
import * as sequencesAtypes from 'common/sequences/sequencesReduxActionTypes'
import { LightFlow } from 'shared/api/common/schemas/flow'
import { ILimitedList } from 'utils/factory'

import {
  exportFlow,
  exportFlowComplete,
  fetchFlow,
  fetchFlowContentData,
  fetchFlowDependencies,
  fetchFullFlow,
  flowPermanentlyRemoved,
  flowRemoved,
  flowRestored,
  importSharedFlow,
  resetFlow,
  setDraftSuccess,
  setFlowHasDraft,
} from '../actions/flowActions'
import {
  updateFlowName,
  updateFlowQuickAutomation,
  updateFlowShared,
  updateFlowSharedCloning,
} from '../actions/flowUpdateActions'

export type IFlowState = Pick<ILimitedList<IFlow & { isFetching?: boolean }>, 'byId'> & {
  exportFlowStatus: Record<string, ExportFlowStatuses>
  sharedFlowImportStatus: Record<string, ExportFlowStatuses>
}

const initialState: IFlowState = {
  byId: {},
  exportFlowStatus: {},
  sharedFlowImportStatus: {},
}

const FOLLOW_WELCOME_STATS_KEYS = ['ctr', 'runs', 'clicked_unq'] as const

interface IEntityWithFlow {
  flow: null | LightFlow
}

export const addFlowsToState = (items: IEntityWithFlow[], state: IFlowState): IFlowState => {
  items.forEach((item) => (state = addFlowToState(item, state)))
  return state
}

export const addFlowToState = (item: IEntityWithFlow, state: IFlowState): IFlowState => {
  if (!item.flow) {
    return state
  }
  const flow = parseFlow(item.flow)
  return dot.merge(state, `byId.${flow.id}`, flow)
}

const isTriggerWithFlow = (
  trigger: IFlowTrigger,
): trigger is Extract<IFlowTrigger, { flowId: string }> => 'flowId' in trigger

const isTriggerWithMultipleFlows = (
  trigger: IFlowTrigger,
): trigger is Extract<IFlowTrigger, { flowIds: string[] }> => 'flowIds' in trigger

export const handleFlowTriggerUpdated = (
  updatedTrigger: IFlowTrigger,
  state: IFlowState,
): IFlowState => {
  const triggerWithMultipleFlows = isTriggerWithMultipleFlows(updatedTrigger)

  if (!isTriggerWithFlow(updatedTrigger) && !triggerWithMultipleFlows) {
    return state
  }

  Object.values(state.byId).forEach((flow) => {
    if (!isFullFlow(flow)) {
      return
    }

    const triggerIndex = flow.triggers.findIndex((t) => t.triggerId === updatedTrigger.triggerId)
    const hasUpdatedTrigger = triggerIndex !== -1

    const { flowId, flowIds } = updatedTrigger

    const triggerBelongsToFlow = triggerWithMultipleFlows
      ? flowIds.includes(flow.id)
      : flow.id === flowId && flowId

    if (hasUpdatedTrigger && triggerBelongsToFlow) {
      state = dot.merge(state, `byId.${flow.id}.triggers.${triggerIndex}`, updatedTrigger)
    } else if (hasUpdatedTrigger && !triggerBelongsToFlow) {
      state = dot.delete(state, `byId.${flow.id}.triggers.${triggerIndex}`)
    } else if (!hasUpdatedTrigger && triggerBelongsToFlow) {
      state = dot.merge(state, `byId.${flow.id}.triggers`, updatedTrigger)
    }
  })

  return state
}

export const handleFlowTriggerRemoved = (triggerId: ITriggerId, state: IFlowState): IFlowState => {
  Object.values(state.byId).forEach((flow) => {
    if (!isFullFlow(flow)) {
      return
    }

    const triggerIndex = flow.triggers.findIndex((t) => t.triggerId === triggerId)
    const hasRemovedTrigger = triggerIndex !== -1

    if (hasRemovedTrigger) {
      state = dot.delete(state, `byId.${flow.id}.triggers.${triggerIndex}`)
    }
  })

  return state
}

export default function flowReducer(state = initialState, action: AnyAction) {
  switch (action.type) {
    case fetchFlow.pending.type: {
      const flowId = action.meta.arg

      if (flowId) {
        return dot.merge(state, `byId.${flowId}`, { isFetching: true })
      }
      break
    }
    case fetchFullFlow.pending.type:
    case fetchFlowContentData.pending.type: {
      const flowId = action.meta.arg.flowId

      if (flowId) {
        return dot.merge(state, `byId.${flowId}`, { isFetching: true })
      }
      break
    }
    case fetchFlow.fulfilled.type:
    case fetchFullFlow.fulfilled.type: {
      if (action.payload) {
        return dot.merge(state, `byId.${action.payload.id}`, {
          ...action.payload,
          isFetching: false,
        })
      }
      break
    }
    case fetchFlow.rejected.type:
      return dot.merge(state, `byId.${action.meta.arg}`, { isFetching: false })
    case fetchFullFlow.rejected.type:
    case fetchFlowContentData.rejected.type: {
      return dot.merge(state, `byId.${action.meta.arg.flowId}`, { isFetching: false })
    }
    case fetchFlowContentData.fulfilled.type: {
      const { contents, root_content_id } = action.payload ?? {}
      const { flowId, flowName, flowPath } = action.meta.arg
      return dot.merge(state, `byId.${flowId}`, {
        id: flowId,
        name: flowName,
        contents,
        root_content_id,
        isFetching: false,
        path: flowPath,
      })
    }
    case updateFlowName.fulfilled.type:
    case updateFlowShared.fulfilled.type:
    case updateFlowSharedCloning.fulfilled.type:
      if (action.payload) {
        return dot.merge(state, `byId.${action.payload.id}`, action.payload)
      }
      break
    case updateFlowQuickAutomation.fulfilled.type: {
      const { id, is_quick_automation } = action.payload
      return state.byId?.[id]
        ? dot.set(state, `byId.${id}.is_quick_automation`, is_quick_automation)
        : state
    }
    case resetFlow.type: {
      const item = action.payload
      return dot.merge(state, `byId.${item.id}`, item)
    }
    case setDraftSuccess.type: {
      const { flowId } = action.payload

      return state.byId?.[flowId] ? dot.set(state, `byId.${flowId}.hasDraft`, true) : state
    }
    case setFlowHasDraft.type: {
      const { flowId, hasDraft } = action.payload
      return dot.set(state, `byId.${flowId}.hasDraft`, hasDraft)
    }
    case fetchFlowDependencies.fulfilled.type: {
      return dot.merge(state, `byId.${action.meta.arg}.dependenciesMap`, action.payload)
    }

    // EXPORT FLOW
    case exportFlow.pending.type: {
      const { flowId } = action.meta.arg
      return dot.set(state, ['exportFlowStatus', flowId], ExportFlowStatuses.PROCESS)
    }
    case exportFlow.rejected.type: {
      const { flowId } = action.meta.arg
      return dot.set(state, ['exportFlowStatus', flowId], ExportFlowStatuses.FAIL)
    }
    case exportFlow.fulfilled.type: {
      const { flowId } = action.meta.arg
      return dot.set(state, ['exportFlowStatus', flowId], ExportFlowStatuses.SUCCESS)
    }
    case exportFlowComplete.type: {
      const { flowId } = action.payload
      return dot.delete(state, ['exportFlowStatus', flowId])
    }

    // SAVE FLOW
    case importSharedFlow.pending.type: {
      const { pageId } = action.meta.arg
      return dot.set(state, ['sharedFlowImportStatus', pageId], ExportFlowStatuses.PROCESS)
    }
    case importSharedFlow.rejected.type: {
      const { pageId } = action.meta.arg
      return dot.set(state, ['sharedFlowImportStatus', pageId], ExportFlowStatuses.FAIL)
    }
    case importSharedFlow.fulfilled.type: {
      const { pageId } = action.meta.arg
      return dot.set(state, ['sharedFlowImportStatus', pageId], ExportFlowStatuses.SUCCESS)
    }

    case keywordsActions.getListFx.fulfilled.type: {
      return addFlowsToState(action.payload, state)
    }

    case loadPostList.fulfilled.type:
    case ensurePostList.fulfilled.type:
      return addFlowsToState(action.payload.items, state)

    case gtAtypes.WIDGETS_FETCH_RESPONSE: {
      return addFlowsToState(action.data ? action.data.items : action.items, state)
    }

    case gtAtypes.WIDGETS_CURRENT_FETCH_RESPONSE: {
      return addFlowToState(action.data.item, state)
    }

    case keywordsActions.fetchKeyword.fulfilled.type: {
      return addFlowToState(action.payload, state)
    }

    // widgets
    case gtAtypes.WIDGETS_CURRENT_CREATE_RESPONSE:
    case gtAtypes.WIDGETS_CURRENT_SAVE_RESPONSE:
    case gtAtypes.WIDGETS_UPDATE_STATUS_RESPONSE:
    case externalTriggersAtypes.EXTERNALTRIGGERS_CURRENT_SAVE_RESPONSE:
    case externalTriggersAtypes.EXTERNALTRIGGERS_CHANGE_STATUS_RESPONSE:
    case externalTriggersAtypes.EXTERNALTRIGGERS_CURRENT_CREATE_RESPONSE: {
      state = handleFlowTriggerUpdated(action.data.item, state)
      return addFlowToState(action.data.item, state)
    }
    case keywordsActions.saveKeywordStatus.fulfilled.type: {
      if (!action.payload) {
        return state
      }
      state = handleFlowTriggerUpdated(action.payload, state)
      return addFlowToState(action.payload, state)
    }
    case keywordsActions.createItemFx.fulfilled.type: {
      state = handleFlowTriggerUpdated(action.payload, state)
      return addFlowToState(action.payload, state)
    }

    case defaultReplyUpdatedNotification.type: {
      state = handleFlowTriggerUpdated(action.payload, state)
      return addFlowToState(action.payload, state)
    }

    case flowAtypes.FLOW_TRIGGER_UPDATED:
    case gtAtypes.WIDGETS_CREATED_NOTIFICATION:
    case gtAtypes.WIDGETS_UPDATED_NOTIFICATION:
    case externalTriggersAtypes.EXTERNALTRIGGERS_CREATED_NOTIFICATION:
    case externalTriggersAtypes.EXTERNALTRIGGERS_CURRENT_UPDATED_NOTIFICATION:
    case cartReplyActionTypes.CART_REPLY_UPDATED_NOTIFICATION:
    case welcomeMessageAtypes.WELCOME_MESSAGE_UPDATED_NOTIFICATION: {
      state = handleFlowTriggerUpdated(action.item, state)
      return addFlowToState(action.item, state)
    }
    case createTriggerRule.fulfilled.type:
    case createdTriggerRuleNotification.type:
    case updateTriggerRuleNotification.type: {
      const addedTrigger = action.payload
      state = handleFlowTriggerUpdated(addedTrigger, state)

      return addFlowToState(addedTrigger, state)
    }
    case keywordsActions.appendItem.type: {
      const addedTrigger = action.payload.item
      state = handleFlowTriggerUpdated(addedTrigger, state)
      return addFlowToState(addedTrigger, state)
    }
    case keywordsActions.updateItem.type: {
      const updatedTrigger = action.payload.data
      state = handleFlowTriggerUpdated(updatedTrigger, state)
      return addFlowToState(updatedTrigger, state)
    }

    case gtAtypes.WIDGETS_DELETE_RESPONSE:
    case gtAtypes.WIDGETS_DELETED_NOTIFICATION: {
      const triggerId: ITriggerId = `${TriggerType.WIDGET}-${action.id}`
      return handleFlowTriggerRemoved(triggerId, state)
    }

    case keywordsActions.deleteItem.type: {
      const triggerId: ITriggerId = `${TriggerType.KEYWORD}-${action.payload.id}`
      return handleFlowTriggerRemoved(triggerId, state)
    }

    case keywordsActions.deleteItemFx.fulfilled.type: {
      const triggerId: ITriggerId = `${TriggerType.KEYWORD}-${action?.meta.arg?.id}`
      return handleFlowTriggerRemoved(triggerId, state)
    }
    case flowAtypes.FLOW_TRIGGER_REMOVED: {
      const triggerId: ITriggerId = `${action.triggerType}-${action.id}` as ITriggerId
      return handleFlowTriggerRemoved(triggerId, state)
    }
    case deleteTriggerRule.fulfilled.type:
    case deletedNotification.type: {
      const triggerId: ITriggerId = `${TriggerType.RULE}-${action?.payload?.id}`
      return handleFlowTriggerRemoved(triggerId, state)
    }
    case externalTriggersAtypes.EXTERNALTRIGGERS_DELETE_RESPONSE:
    case externalTriggersAtypes.EXTERNALTRIGGERS_DELETED_NOTIFICATION: {
      const triggerId: ITriggerId = `${TriggerType.EXTERNAL_TRIGGER}-${action.id}`
      return handleFlowTriggerRemoved(triggerId, state)
    }

    case addPost.fulfilled.type:
      return addFlowToState(action.payload, state)

    case atypes.FS_OBJECT_LOAD_SUCCESS:
    case atypes.FS_RENAME_OBJECT_SUCCESS:
    case sequencesAtypes.SEQUENCE_MESSAGE_SET_SENDING_SETTINGS_RESPONSE: {
      return addFlowToState(action.item, state)
    }
    case flowRemoved.type: {
      return dot.set(state, `byId.${action.payload.substr(1)}.status`, FlowStatus.DELETED)
    }
    case flowPermanentlyRemoved.fulfilled.type: {
      if (action.payload) {
        return dot.set(state, `byId`, omit(state.byId, action.payload))
      }

      return state
    }
    case flowRestored.type: {
      return dot.set(state, `byId.${action.payload.substr(1)}.status`, FlowStatus.ACTIVE)
    }

    case cartReplyActionTypes.CART_REPLY_LOAD_RESPONSE:
    case welcomeMessageAtypes.WELCOME_MESSAGE_LOAD_RESPONSE:
    case welcomeMessageAtypes.WELCOME_MESSAGE_UPDATE_ENABLED_RESPONSE: {
      return addFlowToState(action.data, state)
    }

    case updateTriggerDefaultReplyEnabled.fulfilled.type:
    case loadDefaultReply.fulfilled.type:
    case loadFlow.fulfilled.type:
    case toggleFlow.fulfilled.type: {
      return addFlowToState(action.payload, state)
    }

    case loadTikTokFlow.fulfilled.type:
    case setTikTokFlow.fulfilled.type:
    case removeTikTokFlow.fulfilled.type:
    case toggleTikTokFlow.fulfilled.type:
    case tikTokTriggerUpdated.type: {
      // TikTok responses carry no stats, keep the ones already in the flow
      const trigger = omit(followWelcomeAdapter(action.payload), FOLLOW_WELCOME_STATS_KEYS)

      state = handleFlowTriggerUpdated(trigger, state)
      return addFlowToState(action.payload, state)
    }

    case `${smrTypes.STORY_MENTION_REPLY_FETCH}_RESPONSE`:
    case `${smrTypes.STORY_MENTION_REPLY_UPDATE_ENABLED}_RESPONSE`:
    case `${smrTypes.STORY_MENTION_REPLY_CHANGE_TYPE}_RESPONSE`:
    case `${smrTypes.STORY_MENTION_REPLY_SET_FLOW}_RESPONSE`: {
      state = handleFlowTriggerUpdated(action.data, state)
      return addFlowToState(action.data, state)
    }

    case sequencesAtypes.SEQUENCE_CREATE_SUCCESS:
    case sequencesAtypes.SEQUENCE_LOAD_ITEM_SUCCESS:
    case sequencesAtypes.SEQUENCE_UPDATE_SUCCESS:
    case sequencesAtypes.SEQUENCE_REORDER_MESSAGES_SUCCESS:
    case sequencesAtypes.SEQUENCE_PATCH_MESSAGE_RESPONSE:
    case sequencesAtypes.SEQUENCE_UPDATED_NOTIFICATION: {
      return addFlowsToState(action.item.sequence_messages ?? [], state)
    }

    case builderAtypes.SET_QUICK_CAMPAIGN_ID_RESPONSE: {
      return dot.merge(state, `byId.${action.flowId}.quick_campaign_data`, {
        quick_campaign_id: action.campaignId,
      })
    }
  }
  return state
}
